import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { eurosToCents, formatCentsAsEuros } from '@/utils/money';

/**
 * Currency text field that stores integer cents but keeps the raw typed string
 * while focused. Reformatting from cents on every keystroke scrambles digits
 * (#1068) — this hook only writes the formatted value on blur / reset.
 */
export function useCentsInput(
  cents: number,
  onCentsChange: (cents: number) => void,
  resetKey?: string | number | boolean,
) {
  const [text, setText] = useState(() => formatCentsAsEuros(cents));
  const focusedRef = useRef(false);
  const onCentsChangeRef = useRef(onCentsChange);
  onCentsChangeRef.current = onCentsChange;

  useEffect(() => {
    if (!focusedRef.current) {
      setText(formatCentsAsEuros(cents));
    }
  }, [cents]);

  useEffect(() => {
    focusedRef.current = false;
    setText(formatCentsAsEuros(cents));
    // Intentionally only when the caller remounts the field (dialog open / row id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const commit = useCallback((raw: string, fallback = 0): number => {
    const parsed = eurosToCents(raw);
    const next = parsed ?? fallback;
    onCentsChangeRef.current(next);
    setText(formatCentsAsEuros(next));
    return next;
  }, []);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setText(raw);
    const parsed = eurosToCents(raw);
    if (parsed !== null) {
      onCentsChangeRef.current(parsed);
    }
  }, []);

  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const onBlur = useCallback(() => {
    focusedRef.current = false;
    commit(text);
  }, [commit, text]);

  return { value: text, onChange, onFocus, onBlur, commit } as const;
}
